import React, { useContext, useEffect, useState } from "react";
import api from "../../services/api";
import { AuthContext } from "../../context/Auth/AuthContext";

const money = value =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL"
  }).format(Number(value || 0));

const percent = value => `${Number(value || 0).toFixed(2)}%`;

const currentMonth = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const cardStyle = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 16,
  padding: 16,
  boxShadow: "0 10px 30px rgba(15, 23, 42, 0.06)"
};

const SellerGoalsDashboard = () => {
  const { user } = useContext(AuthContext);
  const canEdit = user?.profile === "admin";

  const [month, setMonth] = useState(currentMonth());
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState({
    summary: {},
    sellers: []
  });

  const summary = data.summary || {};
  const sellers = data.sellers || [];

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/dashboard/seller-goals?month=${month}`);
      setData(response.data || {});
    } catch (err) {
      console.error("Erro ao carregar metas:", err);
    } finally {
      setLoading(false);
    }
  };

  const updateGoal = (userId, value) => {
    setData({
      ...data,
      sellers: sellers.map(seller =>
        seller.userId === userId
          ? { ...seller, goalAmount: Number(value || 0) }
          : seller
      )
    });
  };

  const saveGoals = async () => {
    try {
      setSaving(true);

      await api.post("/dashboard/seller-goals", {
        month,
        goals: sellers.map(seller => ({
          userId: seller.userId,
          goalAmount: Number(seller.goalAmount || 0)
        }))
      });

      await loadData();
      alert("Metas salvas com sucesso.");
    } catch (err) {
      console.error("Erro ao salvar metas:", err);
      alert("Erro ao salvar metas. Confirme se você é administrador.");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  return (
    <div style={{ marginTop: 24, marginBottom: 32, gridColumn: "1 / -1", width: "100%" }}>
      <div
        style={{
          background: "linear-gradient(135deg, #111827 0%, #991b1b 55%, #dc2626 100%)",
          borderRadius: 24,
          padding: 24,
          color: "#fff",
          boxShadow: "0 20px 45px rgba(220, 38, 38, 0.22)",
          marginBottom: 18
        }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 16, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontSize: 13, opacity: 0.85, fontWeight: 700 }}>
              Metas Comerciais
            </div>

            <h2 style={{ margin: "6px 0 4px", fontSize: 28, fontWeight: 900 }}>
              Metas dos Vendedores
            </h2>

            <div style={{ opacity: 0.9 }}>
              Meta mensal, valor vendido, percentual atingido e quanto falta vender.
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, alignItems: "end", flexWrap: "wrap" }}>
            <div>
              <div style={{ fontSize: 12, marginBottom: 4 }}>Mês</div>
              <input
                type="month"
                value={month}
                onChange={e => setMonth(e.target.value)}
                style={{
                  border: "1px solid rgba(255,255,255,0.35)",
                  background: "rgba(255,255,255,0.12)",
                  color: "#fff",
                  borderRadius: 10,
                  padding: "10px 12px"
                }}
              />
            </div>

            <button
              onClick={loadData}
              disabled={loading}
              style={{
                border: 0,
                borderRadius: 12,
                padding: "11px 18px",
                fontWeight: 800,
                cursor: "pointer",
                background: "#fff",
                color: "#b91c1c"
              }}
            >
              {loading ? "Carregando..." : "Filtrar"}
            </button>

            {canEdit && (
              <button
                onClick={saveGoals}
                disabled={saving}
                style={{
                  border: 0,
                  borderRadius: 12,
                  padding: "11px 18px",
                  fontWeight: 800,
                  cursor: "pointer",
                  background: "#16a34a",
                  color: "#fff"
                }}
              >
                {saving ? "Salvando..." : "Salvar metas"}
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 14, marginBottom: 16 }}>
        <div style={cardStyle}>
          <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>Meta total</div>
          <div style={{ color: "#0f172a", fontSize: 24, fontWeight: 900 }}>{money(summary.totalGoal)}</div>
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>Vendido</div>
          <div style={{ color: "#16a34a", fontSize: 24, fontWeight: 900 }}>{money(summary.totalSales)}</div>
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>Atingimento</div>
          <div style={{ color: "#dc2626", fontSize: 24, fontWeight: 900 }}>{percent(summary.progressPercentage)}</div>
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>Falta vender</div>
          <div style={{ color: "#ef4444", fontSize: 24, fontWeight: 900 }}>{money(summary.totalMissing)}</div>
        </div>

        <div style={cardStyle}>
          <div style={{ color: "#64748b", fontSize: 12, fontWeight: 700 }}>Média diária necessária</div>
          <div style={{ color: "#f59e0b", fontSize: 24, fontWeight: 900 }}>{money(summary.requiredDailyAverage)}</div>
        </div>
      </div>

      <div style={cardStyle}>
        <div style={{ color: "#64748b", fontSize: 13, fontWeight: 700 }}>
          Ranking de metas
        </div>

        <div style={{ color: "#0f172a", fontSize: 18, fontWeight: 900, marginBottom: 14 }}>
          Meta mensal x realizado por vendedor
        </div>

        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 850 }}>
            <thead>
              <tr style={{ color: "#64748b", textAlign: "left", fontSize: 12 }}>
                <th style={{ padding: "10px 8px" }}>#</th>
                <th style={{ padding: "10px 8px" }}>Vendedor</th>
                <th style={{ padding: "10px 8px" }}>Meta</th>
                <th style={{ padding: "10px 8px" }}>Vendido</th>
                <th style={{ padding: "10px 8px" }}>Vendas</th>
                <th style={{ padding: "10px 8px" }}>Atingimento</th>
                <th style={{ padding: "10px 8px" }}>Falta</th>
                <th style={{ padding: "10px 8px" }}>Status</th>
              </tr>
            </thead>

            <tbody>
              {sellers.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ padding: 16, color: "#64748b" }}>
                    Nenhum vendedor encontrado.
                  </td>
                </tr>
              ) : (
                sellers.map((seller, index) => (
                  <tr key={seller.userId} style={{ borderTop: "1px solid #e5e7eb" }}>
                    <td style={{ padding: "12px 8px", fontWeight: 900 }}>{index + 1}</td>

                    <td style={{ padding: "12px 8px", fontWeight: 800 }}>
                      {seller.name}
                    </td>

                    <td style={{ padding: "12px 8px" }}>
                      {canEdit ? (
                        <input
                          type="number"
                          min="0"
                          step="100"
                          value={seller.goalAmount || 0}
                          onChange={e => updateGoal(seller.userId, e.target.value)}
                          style={{
                            width: 120,
                            border: "1px solid #e5e7eb",
                            borderRadius: 10,
                            padding: "8px 10px"
                          }}
                        />
                      ) : (
                        money(seller.goalAmount)
                      )}
                    </td>

                    <td style={{ padding: "12px 8px", color: "#16a34a", fontWeight: 900 }}>
                      {money(seller.salesAmount)}
                    </td>

                    <td style={{ padding: "12px 8px" }}>{seller.salesCount || 0}</td>

                    <td style={{ padding: "12px 8px", color: seller.progressPercentage >= 100 ? "#16a34a" : "#dc2626", fontWeight: 900 }}>
                      {percent(seller.progressPercentage)}
                    </td>

                    <td style={{ padding: "12px 8px", color: "#ef4444", fontWeight: 900 }}>
                      {money(seller.missingAmount)}
                    </td>

                    <td style={{ padding: "12px 8px" }}>
                      {seller.achieved ? "Meta batida" : seller.hasGoal ? "Em busca" : "Sem meta"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default SellerGoalsDashboard;
